import React, { useEffect, useState } from "react";
import { Button, Form, Input, Modal } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import UploadLogoBox from "../../Markets/components/UploadLogoBox";
import type { Category } from "types/category";

export type AddCategoryFormValues = {
  image?: { originFileObj?: File }[];
  name: string;
  clearImage?: boolean;
};

export type AddCategorySubmitValues = AddCategoryFormValues & {
  clearImage?: boolean;
};

export type AddCategoryModalProps = {
  open: boolean;
  mode: "add" | "edit";
  initialCategory?: Category | null;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (values: AddCategorySubmitValues) => void;
};

const normFile = (e: unknown) => {
  if (Array.isArray(e)) return e;
  const ev = e as { fileList?: { originFileObj?: File }[] };
  return ev?.fileList ?? [];
};

export const AddCategoryModal: React.FC<AddCategoryModalProps> = ({
  open,
  mode,
  initialCategory,
  loading = false,
  onClose,
  onSubmit,
}) => {
  const [form] = Form.useForm<AddCategoryFormValues>();
  const [clearImage, setClearImage] = useState(false);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setClearImage(false);
      return;
    }
    if (mode === "edit" && initialCategory) {
      form.setFieldsValue({
        name: initialCategory.name,
        image: [],
      });
      setClearImage(false);
    } else {
      form.resetFields();
      setClearImage(false);
    }
  }, [open, mode, initialCategory, form]);

  const handleCancel = () => {
    onClose();
    form.resetFields();
    setClearImage(false);
  };

  const handleFinish = (values: AddCategoryFormValues) => {
    onSubmit({ ...values, clearImage });
  };

  return (
    <Modal
      title={mode === "edit" ? "Edit Category" : "Add Category"}
      open={open}
      onCancel={handleCancel}
      footer={null}
      centered
      width={480}
      destroyOnClose
      className="add-category-modal modal-round"
      closeIcon={<CloseOutlined className="add-category-close-icon" />}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
      >
        <Form.Item
          name="image"
          valuePropName="fileList"
          getValueFromEvent={normFile}
          rules={[
            {
              validator: (_, value) => {
                const hasNewFile = Array.isArray(value) && value.length > 0;
                const hasExistingLogo =
                  mode === "edit" && initialCategory?.logo && !clearImage;

                if (hasNewFile || hasExistingLogo) {
                  return Promise.resolve();
                }

                return Promise.reject(new Error("Please upload category logo"));
              },
            },
          ]}
        >
          <UploadLogoBox
            initialImageUrl={
              mode === "edit" && initialCategory?.logo && !clearImage
                ? initialCategory.logo
                : undefined
            }
            onClearInitial={() => setClearImage(true)}
          />
        </Form.Item>

        <Form.Item
          name="name"
          rules={[{ required: true, message: "Please enter product name" }]}
        >
          <Input
            placeholder="Product Name"
            size="large"
            className="input-round"
          />
        </Form.Item>

        <Form.Item className="mb-0 add-category-modal-footer">
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            className="add-category-submit-btn"
            loading={loading}
            disabled={loading}
          >
            {mode === "edit" ? "Update" : "Add Category"}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddCategoryModal;
